import routes from 'common/routes'
import toast from 'react-hot-toast'
import { handle } from 'common/utils'

import OpenGraph from 'components/OpenGraph'
import { EmptyPage } from 'components/Empty'
import { useCurrentUserId } from 'universal/hooks'
import {
  getMostFollowedUsers,
  getUser,
  getUserProfile,
  getUserProfileReviewCounts,
  getProfileReviewForUser,
  useUserById,
  useDoesFollow,
} from 'utils/hooks'
import { days, hours, minutes } from 'common/utils/dates'
import BoldHeaderStripe, {
  stripeClasses,
} from 'components/PageLayouts/BoldHeaderStripe'
import useReviewPageTheme from 'utils/hooks/useReviewPageTheme'
import { handleFallback } from 'utils/pages'
import VoteArrowOverview from 'cc/VoteArrowOverview'

import { UserHeader } from 'components/Header'
import PageSection from 'cc/PageSection'

import FollowButton from 'components/FollowButton'
import Hashtags from 'cc/Hashtags'

import { useLocallyTrackedFollowing } from 'utils/hooks'
import { useHasMounted } from 'common/hooks'
import UserProfileReview from 'components/UserProfileReview'

import Avatar from 'cc/Avatar'
import { useCallback, useMemo, useState } from 'react'
import { debounce } from 'lodash'

import { cn } from 'common/utils'
import ColorSwatchPicker from 'components/ColorSwatchPicker'
import { updateUserColor } from 'common/siteApi'

const WrappedUserHeader = ({ user, uid, doesFollow }) => {
  const hasMounted = useHasMounted()

  // Local state for optimistic updates + not requiring listen: true on doesFollow
  const [showAsFollowing, setShowAsFollowing] =
    useLocallyTrackedFollowing(doesFollow)

  const actionLink =
    !hasMounted || // Don't show the action link from server-side rendering
    !uid ||
    user?.id === uid ||
    doesFollow === undefined ? null : (
      <FollowButton
        uid={uid}
        user={user}
        doesFollow={doesFollow} // for timestamp
        showAsFollowing={showAsFollowing}
        setShowAsFollowing={setShowAsFollowing}
      />
    )

  return (
    <>
      <UserHeader user={user} rightmost={actionLink} big className="text-xl" />
    </>
  )
}

const DEFAULT_COLOR = '#185ca0'

// TODO: the way we pass this as an inline render to Header cannot be efficient...
const UserPageHeader = ({ user, color, reviewCounts }) => {
  const colorStyle = useMemo(
    () => (color ? { borderColor: color } : {}),
    [color],
  )

  return (
    <div className="flex flex-col items-center space-y-2">
      <Avatar
        user={user}
        size={180}
        className={cn(
          'rounded-full shadow-xl',
          color ? 'border-4' : 'border-blue-700 border-2',
        )}
        style={colorStyle}
      />

      <div className="font-serif text-3xl text-gray-800 hover:text-gray-900">
        {handle(user)}
      </div>

      <VoteArrowOverview
        reviewCounts={reviewCounts}
        className="flex p-5 justify-center space-x-5"
      />
    </div>
  )
}

const StripeColorPicker = ({ color, handleChange }) => {
  const colorStyle = useMemo(
    // NOTE: can also leverage tailwind to support gradients:
    // () => ({ '--tw-gradient-to': color, backgroundColor: color }),
    () => ({
      backgroundColor: color,
    }),
    [color],
  )

  return (
    <div
      className={cn(stripeClasses, 'flex justify-end items-center px-5')}
      style={colorStyle}
    >
      <ColorSwatchPicker color={color} onChange={handleChange} />
    </div>
  )
}

const UserPage = ({
  preloadedUser,
  preloadedReview,
  profile,
  reviewCounts,
}) => {
  const uid = useCurrentUserId()

  // NOTE: so initialData doesn't work as anticipated/maybe at all; here we're immediately re-connected & duplicating the query,
  // but we need it live and the SSG version is ideally days old rather than seconds, so it's hopefully not a duplicative as it seems....?
  const { data: clientUser } = useUserById(preloadedUser?.id, {
    listen: !!uid, // Only listen if we're logged in
  })

  // TODO: is it worth connecting client-side here at all anymore?
  const user = clientUser || preloadedUser

  const doesFollow = useDoesFollow({
    uid,
    toBeFollowedUid: user.id,
    listen: !!uid,
  })

  const [color, setLocalColor] = useState(user?.color)
  // NOTE: can also leverage tailwind to support gradients:
  // () => ({ '--tw-gradient-to': color, backgroundColor: color }),
  const colorStyle = useMemo(
    () => (color ? { backgroundColor: color, backgroundImage: 'none' } : {}),
    [color],
  )

  const debouncedOnChange = useCallback(
    debounce(async (color) => {
      console.log('debouncedOnChange', color)
      try {
        await updateUserColor(color)
        toast.dismiss()
        toast.success('Updated your profile color')
      } catch (e) {
        toast.error(<span>Unable to update: {e.message}</span>)
      }
    }, 500),
    [],
  )
  const handleChange = useCallback(
    (val) => {
      setLocalColor(val)
      debouncedOnChange && debouncedOnChange(val)
    },
    [setLocalColor, debouncedOnChange],
  )

  const stripeContent = useMemo(
    () =>
      uid && user.id === uid ? (
        <StripeColorPicker color={color} handleChange={handleChange} />
      ) : null,
    [uid, user, color, handleChange],
  )

  // ? TODO: add toast about no matching user and redirect back to /users
  // router.push('/users')
  // OR: build a better 404-ish page (note: SSG should handle rendering error before this ever gets called now)
  if (!user) return <EmptyPage>No matching user found.</EmptyPage>

  // TODO: numTotalReviews
  const { numDomainReviews = 0, numPageReviews = 0 } = user.meta

  return (
    <>
      <OpenGraph
        url={routes.userUrl(user)}
        title={handle(user)}
        description={`${handle(user, true)}'s Profile`} // TODO: Netsporation? We need names! Also - dynamic? Most recent review: foo.com rated positively?
        image={user.avatar}
        type="profile"
        profile__username={user.handle}
        profile__tag={user.hashtags} // Not a supported og: tag, but since we have it...

        // Note: if can add multiple images (e.g. user avatar, domain avatar), user can select in sharing dialog
        // article - Namespace URI: https://ogp.me/ns/article#
        // On content, twitter:site (our twitter account handle) and twitter:creator (user's twitter handle)
      />
      <BoldHeaderStripe
        stripeStyle={colorStyle}
        header={
          <UserPageHeader
            user={user}
            reviewCounts={reviewCounts}
            color={color}
          />
        }
        stripeContent={stripeContent}
      >
        <WrappedUserHeader user={user} uid={uid} doesFollow={doesFollow} />
        <PageSection className="shadow-inner bg-opacity-75">
          <Hashtags
            hashtags={profile.hashtags}
            className="space-x-4"
            tagClassName="text-xl hover:text-blue-700"
            link
          />
        </PageSection>
        <UserProfileReview
          numDomainReviews={numDomainReviews}
          numPageReviews={numPageReviews}
          uid={uid}
          user={user}
          profileReview={preloadedReview}
        />
      </BoldHeaderStripe>
    </>
  )
}

UserPage.fullscreen = true
UserPage.domainLogos = true

export async function getStaticPaths() {
  const mostFollowedUsers = await getMostFollowedUsers()

  const paths = mostFollowedUsers.map((user) => ({
    params: { userKey: user.handle },
  }))

  // TODO: switch to fallback: false and implement a fallback page
  // https://nextjs.org/docs/api-reference/data-fetching/get-static-paths#fallback-pages
  return { paths, fallback: 'blocking' }
}

export async function getStaticProps({ params }) {
  const preloadedUser = await getUser(params.userKey)

  if (!preloadedUser?.exists) {
    return { notFound: true }
  }

  const profile = await getUserProfile(preloadedUser.id)

  const preloadedReview =
    (await getProfileReviewForUser(preloadedUser?.id)) || {}

  const reviewCounts = await getUserProfileReviewCounts(preloadedUser.id)

  return {
    props: {
      preloadedUser,
      preloadedReview,
      profile,
      reviewCounts,
    },
    // revalidate: 7 * days, // Hmm... longer, because we reconnect as soon as load, or shorter for the same reason?
    revalidate: 1 * minutes, // TODO: at least for dev mode, helping be more usable since reviewCounts don't get updated on the client side
  }
}

// TODO: add fallback page -- see note above
// export default handleFallback(UserPage, { loadingTitle: 'Loading User...' })
export default UserPage
